import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useEndSessionMutation } from '../../../store/media/media'
import React, { useEffect, useState } from 'react'
import { TrackEvent, useTracking } from './common/tracking'
import { getRoomTrackingDetails } from './tracking'
import { Col, Container, Row } from 'react-bootstrap'
import { Modal } from '../../../components/Modal/Modal'
import { RoomSession } from '@zocks-communications/client-sdk-js'
import { RoomSession as _RoomSession } from '../../../models/Room'
import { useInPersonMeetingFileApp } from '../../in-person-meeting/InPersonMeetingProvider.shared'
import { parseMetadata } from '../../../common/util'

import { parseError } from '../../in-person-meeting/common/error'
import { calcLocalStorageInfoName, generateChunkName } from '../../in-person-meeting/common/util'
import { useLogger } from '../../../components/Logger/hooks/useLogger'

interface EndRoomSessionModalProps {
  roomSession: RoomSession | _RoomSession
  show: boolean
  onSessionEnded: () => void
  onHide: () => void
}

export const EndRoomSessionModal: React.FC<EndRoomSessionModalProps> = ({ roomSession, show, onSessionEnded, onHide }) => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const [endSession, { isLoading, isSuccess }] = useEndSessionMutation()
  const { getAudioChunks, deleteAudioChunks, uploadAudioChunks, uploadLogFile } = useInPersonMeetingFileApp()
  const { trackingClient } = useTracking()
  const { roomId, id: sessionId } = roomSession
  const [inPersonLoading, setInPersonLoading] = useState<boolean>(false)
  const logger = useLogger()
  const { source } = parseMetadata(roomSession.metadata)
  const _onSessionEnd = async () => {
    await logger.info('[EndRoomSessionModal] Session end initiated', {
      source,
      roomId,
      sessionId,
      accountId,
    })

    if (source === 'voice-memo') {
      setInPersonLoading(true)
      await logger.info('[EndRoomSessionModal] Voice memo detected, finishing in-person meeting...', { roomId, sessionId })
      const meetingInfo = window.localStorage.getItem(calcLocalStorageInfoName(accountId, roomId, sessionId))
      const { currentSequence = 1, date = new Date().toISOString() } = JSON.parse(meetingInfo || '{}')
      await finishInPersonMeeting(currentSequence, date)
        .catch((err) => {
          logger.error('[EndRoomSessionModal] Error finishing in-person meeting', err)
          console.error(err)
        })
        .finally(() => {
          setInPersonLoading(false)
        })
    } else {
      await logger.info('[EndRoomSessionModal] Ending session via mutation', { roomId, sessionId, accountId })
      endSession({
        accountId,
        roomId: roomSession.roomId,
        sessionId: roomSession.id,
      })
    }
  }

  useEffect(() => {
    if (isSuccess) {
      onSessionEnded()
    }
  }, [isSuccess, onSessionEnded])

  const onSessionEnd = tracked(_onSessionEnd, {
    eventName: TrackEvent.ROOM_SESSION_ENDED,
    eventDetails: getRoomTrackingDetails(roomSession),
  })

  const finishInPersonMeeting = async (sequence: number, finishedAt: string) => {
    await logger.info('[EndRoomSessionModal] finishInPersonMeeting called', { sequence, finishedAt, roomId, sessionId })
    if (roomSession.isActive) {
      try {
        await logger.info('[EndRoomSessionModal] Aborted in-person-meeting finishing...')
        const chunkFileName = generateChunkName(roomId, sessionId, sequence.toString())
        await logger.info('[EndRoomSessionModal] Fetching audio chunks', { accountId, roomId, sessionId })
        const parts = await getAudioChunks(accountId, roomId, sessionId)
        if (parts && parts.length) {
          await logger.info('[EndRoomSessionModal] Uploading audio chunks', { chunkFileName, partCount: parts.length })
          await uploadAudioChunks(
            accountId,
            roomId,
            sessionId,
            chunkFileName,
            sequence,
            parts.map((p) => new Blob([p.fileContent], { type: p.mimeType })),
          )
        } else {
          await logger.warn('[EndRoomSessionModal] No audio chunks found to upload', { accountId, roomId, sessionId })
        }
        const meetingInfoName = calcLocalStorageInfoName(accountId, roomId, sessionId)
        await logger.info('[EndRoomSessionModal] Deleting audio chunks', { meetingInfoName })
        await deleteAudioChunks(accountId, roomId, sessionId)
        const meetingInfo = window.localStorage.getItem(meetingInfoName)
        window.localStorage.removeItem(meetingInfoName)
        await logger.info('[EndRoomSessionModal] Removed meeting info from localStorage', { meetingInfoName })
        await logger.info('[EndRoomSessionModal] Ending session after in-person meeting finish', { roomId, sessionId, accountId })
        endSession({
          accountId,
          roomId: roomSession.roomId,
          sessionId: roomSession.id,
        })
        trackingClient.track({
          eventName: TrackEvent.ABORTED_IN_PERSON_MEETING_FINISHED,
          eventDetails: {
            forceFinish: true,
            platform: 'desktop',
            roomId: roomId,
            sessionId: sessionId,
            finishedAt: finishedAt,
            meetingInfo: meetingInfo,
          },
        })
        await logger.info('[EndRoomSessionModal] Aborted meeting finished manually.')
      } catch (error: unknown) {
        const { message } = parseError(error)
        await logger.error('[EndRoomSessionModal] Error during finishing meeting', {
          errorMessage: message,
          roomId,
          sessionId,
          finishedAt,
        })
        trackingClient.track({
          eventName: TrackEvent.IN_PERSON_MEETING_FINISHING_ERROR,
          eventDetails: {
            forceFinish: true,
            platform: 'desktop',
            roomId: roomId,
            sessionId: sessionId,
            finishedAt: finishedAt,
            errorMessage: message,
          },
        })
        throw new Error('Error during finishing meeting.')
      } finally {
        const logTraceId = `in-person-meeting-${accountId}-${roomSession.roomId}-${roomSession.id}`
        await logger.info('[EndRoomSessionModal] Uploading log file after meeting finish', { roomId, sessionId })
        const logs = await logger.getLogsByTraceId(logTraceId)
        if (logs) {
          await uploadLogFile(accountId, roomId, sessionId, 'voice-memo-aborted-finish.log', logs)
          await logger.info('[EndRoomSessionModal] Log file uploaded', { logCount: logs.length })
        } else {
          await logger.warn('[EndRoomSessionModal] No logs found for traceId', {
            traceId: `in-person-meeting-${accountId}-${roomSession.roomId}-${roomSession.id}`,
          })
        }
        await logger.info('[EndRoomSessionModal] Remove logsByTraceId', { logTraceId })
        await logger.deleteByTraceId(logTraceId)
      }
    } else {
      await logger.warn('[EndRoomSessionModal] finishInPersonMeeting called but roomSession is not active', { roomId, sessionId })
    }
  }

  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>End Meeting</h4>
        </Col>
      </Row>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>
            Are you sure you wish to remove the AI Assistant from the call? Once removed, the AI Assistant will not be able to rejoin the meeting.
          </div>
        </Col>
      </Row>
    </Container>
  )
  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<></>}
      body={body}
      showFooterActions={true}
      onFooterPrimaryAction={onSessionEnd}
      footerPrimaryActionLoading={isLoading || inPersonLoading}
      footerPrimaryActionTitle='Remove'
      footerPrimaryActionButtonVariant='danger'
    ></Modal>
  )
}
